import { Controller } from "@hotwired/stimulus"
import tinymce from "tinymce"
import "tinymce/models/dom"
import "tinymce/icons/default"
import "tinymce/themes/silver"
import "tinymce/plugins/lists"

const HEADINGS = "h1, h2, h6"

export default class extends Controller {
  static targets = ["body", "toolbar", "page", "thumb", "themeField", "bodyField"]

  connect() {
    tinymce.init({
      target: this.bodyTarget,
      inline: true,
      fixed_toolbar_container_target: this.toolbarTarget,
      toolbar_persist: true,
      toolbar_mode: "wrap",
      menubar: false,
      keep_styles: false,
      plugins: "lists",
      toolbar: "bold italic underline bullist outdent indent | forecolor | fontsize blocks | clearformatting",
      block_formats: "Normal text=p; Title=h1; Subheading=h2; Caption=h6",
      font_size_formats: "8px 9px 10px 11px 12px 13px 14px 16px 18px 20px 22px 24px 28px 32px",
      skin: false,
      skin_url: "/assets/tinymce/skins/ui/oxide",
      branding: false,
      promotion: false,
      setup: (editor) => {
        this.editor = editor
        editor.on("keydown", (event) => this.keepEntryTitleOnOneLine(event))
        editor.on("NewBlock", ({ newBlock }) => this.plainEmptyBlock(newBlock))
        editor.ui.registry.addButton("clearformatting", {
          icon: "remove-formatting",
          tooltip: "Clear formatting",
          onAction: () => this.clearFormatting()
        })
      }
    })
  }

  disconnect() {
    this.editor?.remove()
  }

  switch(event) {
    const template = event.params.template

    this.pageTarget.dataset.theme = template
    this.themeFieldTarget.value = template
    this.thumbTargets.forEach((thumb) => thumb.setAttribute("aria-pressed", thumb === event.currentTarget))
  }

  clearFormatting() {
    const { dom, selection, undoManager } = this.editor

    undoManager.transact(() => {
      this.editor.execCommand("RemoveFormat")
      selection.getSelectedBlocks().filter((block) => block.matches(HEADINGS)).forEach((block) => {
        // Inside a list item the heading is unwrapped, so the item keeps its bullet.
        if (block.parentNode.nodeName === "LI") dom.remove(block, true)
        else dom.rename(block, "p")
      })
    })
    this.editor.nodeChanged()
  }

  keepEntryTitleOnOneLine(event) {
    if (event.key !== "Enter" || event.shiftKey) return

    const { selection } = this.editor
    const line = selection.getNode().closest("h6")
    if (!line) return

    selection.select(line, true)
    selection.collapse(false)
  }

  plainEmptyBlock(block) {
    const { dom, selection } = this.editor
    if (!block?.isConnected || !dom.isEmpty(block)) return
    if (!block.matches(HEADINGS)) return

    const caretInside = block.contains(selection.getNode())
    const paragraph = dom.create("p", {}, '<br data-mce-bogus="1">')
    dom.replace(paragraph, block)
    if (caretInside) selection.setCursorLocation(paragraph, 0)
  }

  sync() {
    this.bodyFieldTarget.value = this.editor.getContent()
  }
}
